import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import { createFilterOptions } from '@mui/material/useAutocomplete'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import CloseIcon from '@mui/icons-material/Close'
import Grid from '@mui/material/Grid2'
import { profileNotesAPI } from 'api/ProfileNotesAPI'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { InputField } from 'Components/reusable/InputFields/InputField'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUser } from 'hooks'
import { useTranslation } from 'react-i18next'
import { IError } from 'types/error'
import { IProfileNote, IProfileNoteTag } from 'types/profileInterfaces'
import NoteItem from './NoteItem'

const CREATE_TAG_SENTINEL_ID = -1
const tagFilter = createFilterOptions<IProfileNoteTag>()

function filterArchivedNotes(notes: IProfileNote[]): IProfileNote[] {
  return notes
    .filter(note => !note.archived)
    .map(note => ({
      ...note,
      replies: filterArchivedNotes(note.replies),
    }))
}

interface IProfileNotesSectionProps {
  personId: number
  enabled: boolean
  canManageNotes: boolean
  canViewArchived: boolean
  isOwnerProfile: boolean
}

const ProfileNotesSection: React.FC<IProfileNotesSectionProps> = ({
  personId,
  enabled,
  canManageNotes,
  canViewArchived,
  isOwnerProfile,
}) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { setSuccessNotificationPopup, setNotificationPopup } = useNotificationPopup()

  const [notes, setNotes] = useState<IProfileNote[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [createMessage, setCreateMessage] = useState<string>('')
  const [createVisibleToOwner, setCreateVisibleToOwner] = useState<boolean>(false)
  const [createTagIds, setCreateTagIds] = useState<number[]>([])
  const [isCreateComposerOpen, setIsCreateComposerOpen] = useState<boolean>(false)
  const [availableTags, setAvailableTags] = useState<IProfileNoteTag[]>([])
  const [replyDraftByNote, setReplyDraftByNote] = useState<Record<number, string>>({})
  const [replyVisibleToOwnerByNote, setReplyVisibleToOwnerByNote] = useState<Record<number, boolean>>({})
  const [replyTagIdsByNote, setReplyTagIdsByNote] = useState<Record<number, number[]>>({})
  const [activeReplyComposerNoteId, setActiveReplyComposerNoteId] = useState<number | null>(null)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<string>('')
  const [editTagIds, setEditTagIds] = useState<number[]>([])

  const translateError = useCallback((error: IError | undefined) => t(error?.key || 'error.generic'), [t])

  const isOwnerOnlyProfileView = isOwnerProfile && !canManageNotes
  const canTagNotes = canManageNotes && !isOwnerOnlyProfileView
  const canCreateTagInline = canTagNotes

  const loadTags = useCallback(
    async (controller?: AbortController) => {
      if (!enabled) {
        setAvailableTags([])
        return
      }

      try {
        const tags = await profileNotesAPI.getTags(personId, controller)
        setAvailableTags(tags)
      } catch (err) {
        const error = err as IError
        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          setNotificationPopup({
            message: translateError(error),
            type: 'error',
            duration: 'long',
          })
        }
      }
    },
    [enabled, personId, setNotificationPopup, translateError]
  )

  const loadNotes = useCallback(
    async (controller?: AbortController) => {
      if (!enabled) {
        setNotes([])
        return
      }

      setLoading(true)
      try {
        const result = await profileNotesAPI.getNotes(personId, controller)
        setNotes(result)
      } catch (err) {
        const error = err as IError

        if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          setNotificationPopup({
            message: translateError(error),
            type: 'error',
            duration: 'long',
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [enabled, personId, setNotificationPopup, translateError]
  )

  useEffect(() => {
    const controller = new AbortController()

    loadNotes(controller)
    loadTags(controller)

    return () => {
      controller.abort()
    }
  }, [enabled, loadNotes, loadTags, personId])

  const ensureTag = async (value: string | IProfileNoteTag): Promise<IProfileNoteTag | null> => {
    if (typeof value !== 'string') {
      return value
    }

    const name = value.trim()
    if (!name) {
      return null
    }

    const existing = availableTags.find(tag => tag.name.toLowerCase() === name.toLowerCase())
    if (existing) {
      return existing
    }

    if (!canCreateTagInline) {
      return null
    }

    const created = await profileNotesAPI.createTag(personId, { name })
    setAvailableTags(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
    return created
  }

  const resolveTagIds = async (values: Array<string | IProfileNoteTag>): Promise<number[]> => {
    const resolved = await Promise.all(values.map(value => ensureTag(value)))
    return [...new Set(resolved.filter(Boolean).map(tag => (tag as IProfileNoteTag).id))]
  }

  const displayedNotes = useMemo(
    () => (canViewArchived && !showArchived ? filterArchivedNotes(notes) : notes),
    [canViewArchived, notes, showArchived]
  )

  const hasAnyNote = useMemo(() => displayedNotes.length > 0, [displayedNotes])

  const handleCreate = async () => {
    const message = createMessage.trim()

    if (!message) {
      return
    }

    try {
      await profileNotesAPI.createNote(personId, {
        message,
        visibleToOwner: createVisibleToOwner,
        tagIds: createTagIds,
      })

      setCreateMessage('')
      setCreateVisibleToOwner(false)
      setCreateTagIds([])
      setIsCreateComposerOpen(false)
      setActiveReplyComposerNoteId(null)
      setSuccessNotificationPopup()
      await loadNotes()
    } catch (err) {
      const error = err as IError
      setNotificationPopup({
        message: translateError(error),
        type: 'error',
        duration: 'long',
      })
    }
  }

  const handleReplyComposerToggle = (noteId: number | null) => {
    // If opening a reply composer, close the new note composer
    if (noteId !== null) {
      setIsCreateComposerOpen(false)
    }
    setActiveReplyComposerNoteId(noteId)
  }

  const handleReply = async (note: IProfileNote) => {
    const noteId = note.id
    const message = (replyDraftByNote[noteId] || '').trim()

    if (!message) {
      return
    }

    try {
      await profileNotesAPI.replyToNote(personId, noteId, {
        message,
        ...(canManageNotes && !isOwnerOnlyProfileView
          ? { visibleToOwner: replyVisibleToOwnerByNote[noteId] ?? note.visibleToOwner }
          : {}),
        ...(canTagNotes ? { tagIds: replyTagIdsByNote[noteId] ?? [] } : {}),
      })
      setReplyDraftByNote(prev => ({ ...prev, [noteId]: '' }))
      setReplyVisibleToOwnerByNote(prev => {
        const { [noteId]: _removed, ...rest } = prev
        return rest
      })
      setReplyTagIdsByNote(prev => {
        const { [noteId]: _removed, ...rest } = prev
        return rest
      })
      setSuccessNotificationPopup()
      await loadNotes()
    } catch (err) {
      const error = err as IError
      setNotificationPopup({
        message: translateError(error),
        type: 'error',
        duration: 'long',
      })
    }
  }

  const handleToggleOwnerVisibility = async (note: IProfileNote) => {
    try {
      await profileNotesAPI.updateNote(personId, note.id, {
        visibleToOwner: !note.visibleToOwner,
      })

      setSuccessNotificationPopup()
      await loadNotes()
    } catch (err) {
      const error = err as IError
      setNotificationPopup({
        message: translateError(error),
        type: 'error',
        duration: 'long',
      })
    }
  }

  const handleEditStart = (note: IProfileNote) => {
    setEditingNoteId(note.id)
    setEditDraft(note.message)
    setEditTagIds(note.parentId === null ? note.tags.map(tag => tag.id) : [])
  }

  const handleEditCancel = () => {
    setEditingNoteId(null)
    setEditDraft('')
    setEditTagIds([])
  }

  const handleEditSave = async (note: IProfileNote) => {
    const message = editDraft.trim()
    if (!message) return

    try {
      await profileNotesAPI.updateNote(personId, note.id, {
        message,
        ...(note.parentId === null ? { tagIds: editTagIds } : {}),
      })
      setEditingNoteId(null)
      setEditDraft('')
      setEditTagIds([])
      setSuccessNotificationPopup()
      await loadNotes()
    } catch (err) {
      const error = err as IError
      setNotificationPopup({
        message: translateError(error),
        type: 'error',
        duration: 'long',
      })
    }
  }

  const handleToggleArchive = async (note: IProfileNote) => {
    try {
      await profileNotesAPI.updateNote(personId, note.id, {
        archived: !note.archived,
      })

      setSuccessNotificationPopup()
      await loadNotes()
    } catch (err) {
      const error = err as IError
      setNotificationPopup({
        message: translateError(error),
        type: 'error',
        duration: 'long',
      })
    }
  }

  if (!enabled) {
    return null
  }

  return (
    <Paper sx={{ p: 2, mt: 2, maxWidth: '1400px !important', m: '0 auto' }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="h5" fontWeight="bold">
            {t('profile.notes.title')}
          </Typography>
        </Grid>

        {canManageNotes && (
          <>
            <Grid size={12}>
              <Stack direction="row" alignItems="center" justifyContent="flex-end">
                {canViewArchived && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showArchived}
                        onChange={e => setShowArchived(e.target.checked)}
                        slotProps={{ input: { 'aria-label': 'profile-note-show-archived' } }}
                      />
                    }
                    label={t('profile.notes.showArchived')}
                    sx={{ mr: 'auto' }}
                  />
                )}
                <Tooltip title={isCreateComposerOpen ? t('profile.notes.editCancel') : t('profile.notes.newNote')}>
                  <IconButton
                    onClick={() => {
                      setIsCreateComposerOpen(prev => !prev)
                      setActiveReplyComposerNoteId(null)
                    }}
                    aria-label={isCreateComposerOpen ? t('profile.notes.editCancel') : t('profile.notes.newNote')}
                  >
                    {isCreateComposerOpen ? <CloseIcon /> : <NoteAddOutlinedIcon />}
                  </IconButton>
                </Tooltip>
              </Stack>

              <Collapse in={isCreateComposerOpen} timeout="auto" unmountOnExit>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <InputField
                    label={t('profile.notes.newNote')}
                    value={createMessage}
                    onChange={e => setCreateMessage(e.target.value)}
                    minRows={3}
                    multiline
                    fullWidth
                    insetLabel
                  />
                  <Autocomplete
                    multiple
                    freeSolo={canCreateTagInline}
                    options={availableTags}
                    value={availableTags.filter(tag => createTagIds.includes(tag.id))}
                    filterOptions={(options, params) => {
                      const filtered = tagFilter(options, params)
                      const trimmed = params.inputValue.trim()
                      const isExisting = options.some(o => o.name.toLowerCase() === trimmed.toLowerCase())
                      if (canCreateTagInline && trimmed && !isExisting) {
                        filtered.push({ id: CREATE_TAG_SENTINEL_ID, name: trimmed } as IProfileNoteTag)
                      }
                      return filtered
                    }}
                    onChange={async (_event, values) => {
                      try {
                        const normalized = (values as Array<string | IProfileNoteTag>).map(v =>
                          typeof v === 'object' && v.id === CREATE_TAG_SENTINEL_ID ? v.name : v
                        )
                        const nextIds = await resolveTagIds(normalized)
                        setCreateTagIds(nextIds)
                      } catch (err) {
                        const error = err as IError
                        setNotificationPopup({
                          message: translateError(error),
                          type: 'error',
                          duration: 'long',
                        })
                      }
                    }}
                    getOptionLabel={option =>
                      typeof option === 'string'
                        ? option
                        : option.id === CREATE_TAG_SENTINEL_ID
                          ? t('profile.notes.createTag', { name: option.name })
                          : option.name
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => {
                      const label =
                        option.id === CREATE_TAG_SENTINEL_ID
                          ? t('profile.notes.createTag', { name: option.name })
                          : option.name

                      return (
                        <li {...props} key={option.id === CREATE_TAG_SENTINEL_ID ? `create-${option.name}` : option.id}>
                          <Chip
                            label={label}
                            size="small"
                            sx={
                              option.id !== CREATE_TAG_SENTINEL_ID && option.color
                                ? { backgroundColor: option.color, color: '#fff' }
                                : undefined
                            }
                          />
                        </li>
                      )
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((tag, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={tag.id}
                          label={tag.name}
                          size="small"
                          sx={tag.color ? { backgroundColor: tag.color, color: '#fff' } : undefined}
                        />
                      ))
                    }
                    renderInput={params => (
                      <InputField
                        {...params}
                        name="profile-note-create-tags"
                        label={t('profile.notes.tags')}
                        insetLabel
                        fullWidth
                        size="small"
                        margin="dense"
                        slotProps={{
                          htmlInput: params?.inputProps,
                          input: params?.InputProps,
                          inputLabel: { shrink: true },
                        }}
                      />
                    )}
                  />
                  <Stack direction="row" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={createVisibleToOwner}
                          onChange={e => setCreateVisibleToOwner(e.target.checked)}
                          slotProps={{ input: { 'aria-label': 'profile-note-visible-to-owner' } }}
                        />
                      }
                      label={t('profile.notes.visibleToOwner')}
                    />
                    <Button variant="contained" onClick={handleCreate} disabled={createMessage.trim().length === 0}>
                      {t('profile.notes.add')}
                    </Button>
                  </Stack>
                  <Divider />
                </Stack>
              </Collapse>
            </Grid>
          </>
        )}

        {loading && (
          <Grid size={12}>
            <Typography>{t('loading')}</Typography>
          </Grid>
        )}

        {!loading && !hasAnyNote && (
          <Grid size={12}>
            <Typography color="text.secondary">{t('profile.notes.noNotes')}</Typography>
          </Grid>
        )}

        {!loading &&
          displayedNotes.map(note => (
            <Grid size={12} key={note.id}>
              <NoteItem
                note={note}
                depth={0}
                canManageNotes={canManageNotes}
                canArchiveNotes={canViewArchived}
                isOwnerProfile={isOwnerOnlyProfileView}
                replyDraftByNote={replyDraftByNote}
                replyVisibleToOwnerByNote={replyVisibleToOwnerByNote}
                onReplyDraftChange={(noteId, value) => setReplyDraftByNote(prev => ({ ...prev, [noteId]: value }))}
                onReplyVisibilityChange={(noteId, visibleToOwner) =>
                  setReplyVisibleToOwnerByNote(prev => ({ ...prev, [noteId]: visibleToOwner }))
                }
                replyTagIdsByNote={replyTagIdsByNote}
                onReplyTagIdsChange={(noteId, tagIds) => setReplyTagIdsByNote(prev => ({ ...prev, [noteId]: tagIds }))}
                onReply={handleReply}
                onToggleVisibility={handleToggleOwnerVisibility}
                onToggleArchive={handleToggleArchive}
                onEditStart={handleEditStart}
                onEditDraftChange={setEditDraft}
                onEditSave={handleEditSave}
                onEditCancel={handleEditCancel}
                availableTags={availableTags}
                canTagNotes={canTagNotes}
                canCreateTagInline={canCreateTagInline}
                editTagIds={editTagIds}
                onEditTagIdsChange={setEditTagIds}
                onResolveTagIds={resolveTagIds}
                editingNoteId={editingNoteId}
                editDraft={editDraft}
                currentAccountId={user?.id}
                activeReplyComposerNoteId={activeReplyComposerNoteId}
                onReplyComposerToggle={handleReplyComposerToggle}
              />
            </Grid>
          ))}
      </Grid>
    </Paper>
  )
}

export default ProfileNotesSection
